// useLookbook.js
//
// PURPOSE:
// Custom hook for managing lookbook data and operations.
// Handles fetching, creating, and managing lookbooks with caching and error handling.
// Provides state management for lookbook list, selected lookbook, and loading states.
//
// SECURITY NOTES:
// - Creates/updates/deletes require authentication token
// - Server-side authorization is verified
// - All API responses are validated
//
// OPTIMIZATION NOTES:
// - Lookbooks are cached in state to avoid redundant API calls
// - Selected lookbook is memoized to prevent unnecessary re-renders
// - useCallback prevents function recreation on every render
// - Prevents duplicate API calls with in-flight request tracking

import { useState, useCallback, useRef } from 'react';
import { 
  getAllLookbooks, 
  getLookbook, 
  createLookbook,
  updateLookbook,
  deleteLookbook
} from '../services/lookbookApi';

export function useLookbook() {
  // Array of all lookbook objects
  const [lookbooks, setLookbooks] = useState([]);

  // Currently selected/opened lookbook for detail view
  const [selectedLookbook, setSelectedLookbook] = useState(null);

  // Loading state for async operations
  const [loading, setLoading] = useState(false);

  // Error message if operation fails
  const [error, setError] = useState(null);

  // Track in-flight requests to prevent duplicate API calls
  const fetchLookupsInFlightRef = useRef(false);
  const fetchLookupsPromiseRef = useRef(null);

  // Fetches all lookbooks from the API
  // SECURITY: No authentication required (public read access)
  // OPTIMIZATION: Results are cached in component state
  // DEDUPLICATION: Prevents multiple simultaneous fetch requests
  const fetchLookbooks = useCallback(async () => {
    // If a fetch is already in progress, return the existing promise
    if (fetchLookupsInFlightRef.current && fetchLookupsPromiseRef.current) {
      return fetchLookupsPromiseRef.current;
    }

    // Mark as in-flight
    fetchLookupsInFlightRef.current = true;
    setLoading(true);
    setError(null);

    const promise = (async () => {
      try {
        const data = await getAllLookbooks();
        setLookbooks(data);
        return data;
      } catch (err) {
        const errorMessage = err.message || 'Failed to fetch lookbooks';
        setError(errorMessage);
        throw err;
      } finally {
        setLoading(false);
        fetchLookupsInFlightRef.current = false;
        fetchLookupsPromiseRef.current = null;
      }
    })();

    // Store promise for concurrent requests
    fetchLookupsPromiseRef.current = promise;
    return promise;
  }, []);

  // Fetches a single lookbook by ID and sets it as selected
  // SECURITY: No authentication required
  // Parameters: lookbook ID (string)
  // Returns: lookbook object or null on error
  const fetchLookbookById = useCallback(async (id) => {
    setLoading(true);
    setError(null);
    try {
      const data = await getLookbook(id);
      setSelectedLookbook(data);
      return data;
    } catch (err) {
      setError(err.message || 'Failed to fetch lookbook');
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  // Creates a new lookbook (admin-only operation)
  // SECURITY: Requires Firebase authentication token
  // Adds the new lookbook to the beginning of the lookbooks array
  // Parameters:
  //   lookbook: object with { name, description, images }
  //   idToken: Firebase authentication token
  // Returns: created lookbook object or null on error
  const addLookbook = useCallback(async (lookbook, idToken) => {
    setLoading(true);
    setError(null);
    try {
      const data = await createLookbook(lookbook, idToken);
      setLookbooks((prev) => [data, ...prev]);
      return data;
    } catch (err) {
      setError(err.message || 'Failed to create lookbook');
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  // Updates an existing lookbook (admin-only operation)
  // SECURITY: Requires Firebase authentication token
  // Updates the lookbook in the array or re-fetches if needed
  // Parameters:
  //   id: lookbook document ID
  //   lookbook: object with updated data
  //   idToken: Firebase authentication token
  // Returns: updated lookbook object or null on error
  const editLookbook = useCallback(async (id, lookbook, idToken) => {
    setLoading(true);
    setError(null);
    try {
      const data = await updateLookbook(id, lookbook, idToken);
      // Update the lookbook in the array
      setLookbooks((prev) =>
        prev.map((item) => (item.id === id ? data : item))
      );
      // Keep selected lookbook in sync without coupling callback to current state.
      setSelectedLookbook((prev) => (prev?.id === id ? data : prev));
      return data;
    } catch (err) {
      setError(err.message || 'Failed to update lookbook');
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  // Deletes a lookbook (admin-only operation)
  // SECURITY: Requires Firebase authentication token
  // Removes the lookbook from the array
  // Parameters:
  //   id: lookbook document ID
  //   idToken: Firebase authentication token
  // Returns: true on success, false on error
  const removeLookbook = useCallback(async (id, idToken) => {
    setLoading(true);
    setError(null);
    try {
      await deleteLookbook(id, idToken);
      // Remove from lookbooks array
      setLookbooks((prev) => prev.filter((item) => item.id !== id));
      // Clear selected if it was deleted.
      setSelectedLookbook((prev) => (prev?.id === id ? null : prev));
      return true;
    } catch (err) {
      setError(err.message || 'Failed to delete lookbook');
      return false;
    } finally {
      setLoading(false);
    }
  }, []);

  // Return all hook state and functions
  return {
    lookbooks,
    selectedLookbook,
    loading,
    error,
    fetchLookbooks,
    fetchLookbookById,
    addLookbook,
    editLookbook,
    removeLookbook,
    setSelectedLookbook,
  };
}

export default useLookbook;
